import {
  DateInput,
  DeleteIcon,
  handleApiRequest,
  Loading,
  PlusIcon,
  SearcherInput,
  TextInput,
  Typography,
  useMobile,
} from "@cpj-ai/panel-npm-package";
import {
  JobPositionInterviewSlotType,
  SetJobPositionCalendarSlotMutation,
  SetJobPositionCalendarSlotMutationVariables,
  useGetJobPositionInterviewTimeSlotsQuery,
  useSetJobPositionCalendarSlotMutation,
} from "@graphql/generated";
import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalProps } from "@heroui/react";
import { theme } from "@theme/index";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import utc from "dayjs/plugin/utc";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { INTERVIEW_DURATION_OPTIONS } from "./constants";
import { addDurationToTime, generateTimeOptions } from "./utils";

dayjs.extend(utc);
dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

type TimesModalProps = Pick<ModalProps, "isOpen" | "onClose" | "onOpenChange"> & {
  jobPostId?: string;
};

export const TimesModal: React.FC<TimesModalProps> = ({ isOpen, onClose, jobPostId }) => {
  const { id } = useParams();
  const {
    data: initialTimeSlots,
    refetch,
    loading,
  } = useGetJobPositionInterviewTimeSlotsQuery({
    variables: {
      id: jobPostId ?? (id as string),
    },
    skip: !jobPostId && !id,
  });

  const [setJobPositionCalendarSlot, { loading: isSaving }] = useSetJobPositionCalendarSlotMutation();
  const isMobile = useMobile();
  const [isFirstDate, setIsFirstDate] = useState(true);

  const [interviewDuration, setInterviewDuration] = useState<string>("45");
  const [groupedTimeSlots, setGroupedTimeSlots] = useState<Record<string, JobPositionInterviewSlotType[]>>({});

  const handleClose = () => {
    onClose?.();
    setIsFirstDate(true);
  };

  const sortGroupedTimeSlots = (slots: Record<string, JobPositionInterviewSlotType[]>) => {
    return Object.keys(slots)
      .sort((a, b) => a.localeCompare(b))
      .reduce((sorted: Record<string, JobPositionInterviewSlotType[]>, date) => {
        const dateSlots = slots[date];
        if (dateSlots) {
          sorted[date] = [...dateSlots].sort((a, b) => {
            const dateA = a.datetime?.[0] || "";
            const dateB = b.datetime?.[0] || "";
            return dateA.localeCompare(dateB);
          });
        }
        return sorted;
      }, {});
  };

  const roundToNearestValidTime = (time: string, selectedDate?: string): string => {
    const currentDateTime = dayjs();
    const timeToCheck = dayjs(`${selectedDate || currentDateTime.format("YYYY-MM-DD")}T${time}`);

    // If the date is today and the time is in the past, use current time
    if (selectedDate && dayjs(selectedDate).isSame(currentDateTime, "day") && timeToCheck.isBefore(currentDateTime)) {
      const roundedTime = roundToNearestValidInterval(currentDateTime);
      return roundedTime.format("HH:mm");
    }

    // Round to the nearest valid 15-minute interval
    return roundToNearestValidInterval(timeToCheck).format("HH:mm");
  };

  const roundToNearestValidInterval = (time: dayjs.Dayjs): dayjs.Dayjs => {
    // Calculate the remainder when divided by 15
    const minutes = time.minute();
    const remainder = minutes % 15;

    // Round to the nearest 15-minute interval
    if (remainder < 7.5) {
      // Round down
      return time.subtract(remainder, "minute");
    } else {
      // Round up
      return time.add(15 - remainder, "minute");
    }
  };

  const addDate = (date: string, start: string) => {
    // Ensure the start time is a valid 15-minute interval
    const validStart = roundToNearestValidTime(start, date);

    setGroupedTimeSlots((prevSlots) => {
      const newSlots = { ...prevSlots };
      if (!newSlots[date]) {
        newSlots[date] = [];
      }

      const duration = Number(interviewDuration);
      const validDuration = isNaN(duration) ? 45 : duration;

      const startDateTime = `${date}T${validStart}`;
      const endDateTime = `${date}T${addDurationToTime(validStart, validDuration)}`;

      // Existing conflict checking logic remains the same
      const isTimeSlotAvailable = !newSlots[date].some((slot) => {
        const existingSlotStart = dayjs(slot.datetime[0]);
        const existingSlotEnd = dayjs(slot.datetime[1]);
        const newSlotStart = dayjs(startDateTime);
        const newSlotEnd = dayjs(endDateTime);

        return (
          newSlotStart.isBetween(existingSlotStart, existingSlotEnd, null, "[)") ||
          newSlotEnd.isBetween(existingSlotStart, existingSlotEnd, null, "(]") ||
          (newSlotStart.isSameOrBefore(existingSlotStart) && newSlotEnd.isSameOrAfter(existingSlotEnd))
        );
      });

      if (!isTimeSlotAvailable) {
        console.warn("Time slot conflicts with existing slots");
        return prevSlots;
      }

      newSlots[date].push({
        id: uuidv4(),
        datetime: [startDateTime, endDateTime],
        isAvailable: true,
      });

      return newSlots;
    });
  };

  const editDate = (slotId: string, date: string, newTime: string) => {
    // Ensure the new time is a valid 15-minute interval
    const validTime = roundToNearestValidTime(newTime, date);

    setGroupedTimeSlots((prevSlots) => {
      const newSlots = { ...prevSlots };
      const dateSlots = newSlots[date];

      const slotIndex = dateSlots?.findIndex((slot) => slot.id === slotId);
      if (!dateSlots || slotIndex === undefined || slotIndex === -1) return prevSlots;

      const currentSlot = dateSlots[slotIndex];
      if (!currentSlot) return prevSlots;

      const duration = Number(interviewDuration);
      const validDuration = isNaN(duration) ? 45 : duration;

      const startDateTime = `${date}T${validTime}`;
      const endDateTime = `${date}T${addDurationToTime(validTime, validDuration)}`;

      // Existing conflict checking logic remains the same
      const isTimeSlotAvailable = !dateSlots.some((slot, index) => {
        if (index === slotIndex) return false;

        const existingSlotStart = dayjs(slot.datetime[0]);
        const existingSlotEnd = dayjs(slot.datetime[1]);
        const newSlotStart = dayjs(startDateTime);
        const newSlotEnd = dayjs(endDateTime);

        return (
          newSlotStart.isBetween(existingSlotStart, existingSlotEnd, null, "[)") ||
          newSlotEnd.isBetween(existingSlotStart, existingSlotEnd, null, "(]") ||
          (newSlotStart.isSameOrBefore(existingSlotStart) && newSlotEnd.isSameOrAfter(existingSlotEnd))
        );
      });

      if (!isTimeSlotAvailable) {
        console.warn("Time slot conflicts with existing slots");
        return prevSlots;
      }

      // Update the specific slot
      dateSlots[slotIndex] = {
        ...currentSlot,
        datetime: [startDateTime, endDateTime],
      };

      return newSlots;
    });
  };

  const removeDate = (id: string) => {
    setGroupedTimeSlots((prevSlots) => {
      const newSlots = Object.entries(prevSlots).reduce(
        (acc, [date, slots]) => {
          const filteredSlots = slots.filter((slot) => slot.id !== id);
          if (filteredSlots.length > 0) {
            acc[date] = filteredSlots;
          }
          return acc;
        },
        {} as Record<string, JobPositionInterviewSlotType[]>,
      );

      return sortGroupedTimeSlots(newSlots);
    });
  };

  const resetDates = () => {
    setGroupedTimeSlots({});
  };

  const handleSave = () => {
    handleApiRequest<SetJobPositionCalendarSlotMutation, SetJobPositionCalendarSlotMutationVariables>({
      apiFunction: setJobPositionCalendarSlot,
      variables: {
        jobPositionId: id as string,
        slots: Object.values(groupedTimeSlots)
          .flat()
          .filter((slot) => slot.isAvailable)
          .map((slot) =>
            slot.datetime.map((item: string) => {
              return dayjs(item).utc();
            }),
          ),
      },
      componentName: "JobPositionView",
      actionName: "setJobPositionCalendarSlot",
      onSuccess: () => {
        handleClose();
      },
      successMessage: "Interview times saved successfully",
      fallbackErrorMessage: "Failed to save interview times",
    });
  };

  useEffect(() => {
    const hasInterviews = !!initialTimeSlots?.organization?.jobPosition?.get?.interviewSlots.length;

    if (!hasInterviews) {
      setInterviewDuration("45");
      setGroupedTimeSlots({});
      return;
    }

    const duration =
      dayjs(initialTimeSlots?.organization?.jobPosition?.get?.interviewSlots[0]?.datetime[1]).diff(
        dayjs(initialTimeSlots?.organization?.jobPosition?.get?.interviewSlots[0]?.datetime[0]),
        "minutes",
      ) ?? 45;
    setInterviewDuration(duration.toString());

    const slots = initialTimeSlots?.organization?.jobPosition?.get?.interviewSlots ?? [];

    const groupedByDate = slots.reduce((groups: Record<string, JobPositionInterviewSlotType[]>, slot) => {
      const date = slot.datetime[0]?.split("T")[0];
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(slot);
      return groups;
    }, {});

    setGroupedTimeSlots(sortGroupedTimeSlots(groupedByDate));
  }, [initialTimeSlots, isOpen]);

  useEffect(() => {
    refetch();
  }, [isOpen, refetch]);

  const disableDuration =
    Object.keys(groupedTimeSlots).find((item) => groupedTimeSlots[item]?.find((slot) => !slot.isAvailable)) !==
    undefined;

  const noNewInterviews =
    Object.keys(groupedTimeSlots).length === 0 &&
    !initialTimeSlots?.organization?.jobPosition?.get?.interviewSlots.length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      isDismissable={false}
      size={isMobile ? "full" : "5xl"}
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader>Manage Interview Times</ModalHeader>
        {loading ? (
          <Loading />
        ) : (
          <ModalBody>
            <div className="flex flex-col gap-2 w-full">
              <Typography className="font-semibold">Interview Duration:</Typography>
              <SearcherInput
                className="w-96"
                label="How much time do you need for each interview?"
                labelKey="label"
                valueKey="value"
                isClearable={false}
                items={INTERVIEW_DURATION_OPTIONS}
                isDisabled={disableDuration}
                onChange={(value) => {
                  const durationValue =
                    typeof value === "object" && value !== null && "value" in value ? value.value : value;
                  setInterviewDuration(durationValue as string);
                  resetDates();
                }}
                value={interviewDuration}
              />

              <div className="flex flex-col gap-2 my-2">
                <Typography className="font-semibold">Interview Times:</Typography>
              </div>
              <div className="flex flex-col gap-2">
                {Object.keys(groupedTimeSlots).map((date) => (
                  <div
                    key={date}
                    className="grid grid-cols-1 lg:grid-cols-3 gap-2 border-1 p-2 rounded-md shadow-sm border-gray-300 bg-zinc-50 pb-4"
                  >
                    <div className="col-span-1 flex flex-col">
                      <Typography className="text-base text-gray-500 mb-2 pt-2 pl-2">
                        Date: {dayjs(date).format("ddd YYYY/MM/DD")}
                      </Typography>
                      <Button
                        type="button"
                        className="w-fit ml-2"
                        variant="flat"
                        color="success"
                        onPress={() => {
                          const dateSlots = groupedTimeSlots[date];
                          if (dateSlots && dateSlots.length > 0) {
                            const lastSlot = dateSlots[dateSlots.length - 1];
                            addDate(date, lastSlot?.datetime[1]?.split("T")[1] ?? "09:00");
                          } else {
                            addDate(date, "09:00");
                          }
                        }}
                      >
                        <PlusIcon width={20} height={20} viewBox="0 0 20 20" className="stroke-green-600" />
                        Add New Time
                      </Button>
                    </div>
                    <div className="col-span-1 lg:col-span-2  flex flex-col gap-2 w-full">
                      {groupedTimeSlots[date]?.map((slot) => {
                        return (
                          <div key={slot.id} className="w-full flex flex-row justify-start items-end gap-2">
                            <div className="w-2/3">
                              <SearcherInput
                                label="Start Time:"
                                isClearable={false}
                                isDisabled={!slot.isAvailable}
                                onChange={(value) => {
                                  if (Array.isArray(value) || typeof value === "string") return;
                                  const timeValue =
                                    typeof value === "object" && value !== null && "value" in value
                                      ? value.value
                                      : value;
                                  editDate(slot.id, date, timeValue ?? "");
                                }}
                                value={dayjs(slot.datetime[0]).format("HH:mm")}
                                items={generateTimeOptions(date)}
                                valueKey="value"
                                labelKey="label"
                              />
                            </div>
                            <div className="w-1/3">
                              <TextInput
                                label="End Time:"
                                isDisabled
                                value={dayjs(slot.datetime[1]).format("hh:mm a")}
                              />
                            </div>
                            <Button
                              type="button"
                              isDisabled={!slot.isAvailable}
                              variant="bordered"
                              className="w-fit lg:w-fit min-w-9 py-2 px-2 h-fit rounded-lg border-2 border-primary-500"
                              onPress={() => removeDate(slot.id)}
                            >
                              <DeleteIcon
                                width={20}
                                height={20}
                                viewBox="0 0 20 20"
                                stroke={theme.themes?.light?.colors?.primary?.[500]}
                              />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </ModalBody>
        )}
        <ModalFooter className="flex items-center">
          <DateInput
            minDate={dayjs().startOf("day").toDate()}
            isDisabled={loading}
            onChange={(date: any) => {
              if (isFirstDate) {
                setIsFirstDate(false);
                return;
              }
              if (date) {
                // Determine the initial time
                const initialTime = dayjs().isSame(date, "day") ? dayjs().add(30, "minute").format("HH:mm") : "09:00";

                addDate(dayjs(date).format("YYYY-MM-DD"), initialTime);
              }
            }}
            render={
              <Button variant="bordered" color="success">
                Add New Interview Date
              </Button>
            }
          />
          <Button
            color="success"
            onPress={handleSave}
            isLoading={isSaving}
            isDisabled={isSaving || loading || noNewInterviews}
          >
            Save & Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
